/**
 * Build a short accessibility summary from land-cover year rows.
 * Pure / data-driven — no hard-coded narrative.
 */
export function summarizeLandCoverTrend(
  byYear: readonly Record<string, string | number>[],
  classes: readonly string[],
): string {
  if (byYear.length < 2 || classes.length === 0) {
    return "Pas assez de points temporels pour résumer l’évolution.";
  }
  const first = byYear[0]!;
  const last = byYear[byYear.length - 1]!;
  const from = String(first.year);
  const to = String(last.year);

  const deltas = classes
    .map((cls) => {
      const a = Number(first[cls]);
      const b = Number(last[cls]);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
      return { cls, delta: b - a, a, b };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null)
    .sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));

  if (deltas.length === 0) {
    return `Aucune série comparable entre ${from} et ${to}.`;
  }

  const up = deltas.find((d) => d.delta > 0);
  const down = deltas.find((d) => d.delta < 0);
  const parts: string[] = [`Entre ${from} et ${to}`];
  if (up) {
    parts.push(`${classPhrase(up.cls)} ont augmenté (${up.a.toFixed(1)} → ${up.b.toFixed(1)})`);
  }
  if (down) {
    parts.push(
      `${classPhrase(down.cls)} ont diminué (${down.a.toFixed(1)} → ${down.b.toFixed(1)})`,
    );
  }
  if (!up && !down) {
    parts.push("les classes sélectionnées sont restées stables");
  }
  return `${parts.join(", ")}.`;
}

function classPhrase(label: string): string {
  const normalized = label.trim();
  if (!normalized) return "les surfaces";
  return `les ${normalized.charAt(0).toLocaleLowerCase("fr")}${normalized.slice(1)}`;
}

export function canStackLandCoverSeries(metric: string, classCount: number): boolean {
  // Stacking only makes sense for share components of one total (same %).
  return metric === "part" && classCount >= 2;
}
